import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const UserProvider = ({ children }) => {
  // Mock user for bypass - remove when Firebase is added
  const mockUser = {
    id: 'mock-user-123',
    username: 'testuser',
    email: 'test@example.com', 
    phone: '+91 9876543210',
    full_name: 'Test User',
    verification_level: 'KYC_VERIFIED',
    phone_verified: true,
    email_verified: true,
    kyc_verified: true,
    total_contests: 15,
    total_winnings: 2500,
    referral_code: 'TEST123',
    created_at: new Date().toISOString(),
    address: '123 Test Street',
    city: 'Mumbai',
    state: 'Maharashtra',
    pincode: '400001'
  };

  const [user, setUser] = useState(mockUser); // Always provide mock user
  const [loading, setLoading] = useState(false); // Never show loading
  const [notifications, setNotifications] = useState([
    {
      id: '1',
      title: 'Welcome to WinQuizz!',
      message: 'Your account has been created successfully.',
      notification_type: 'SYSTEM',
      read: false,
      created_at: new Date().toISOString()
    },
    {
      id: '2', 
      title: 'Contest Starting Soon',
      message: 'Mega GK Contest starts in 2 hours!',
      notification_type: 'CONTEST',
      read: false,
      created_at: new Date().toISOString()
    }
  ]);
  const [unreadCount, setUnreadCount] = useState(2);

  // Setup axios interceptor for auth token
  useEffect(() => {
    const token = localStorage.getItem('userToken');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    // Response interceptor to handle auth errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Fetch notifications periodically
  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [user]);

  // Bypass all auth checks - mock functions for testing
  const checkAuthStatus = async () => {
    // Mock function - do nothing
  };

  const register = async (userData) => {
    // Mock registration success
    return { success: true, user: mockUser };
  };

  const login = async (credentials) => {
    // Mock login success
    return { success: true, user: mockUser };
  };

  const logout = () => {
    // Mock logout - redirect to home but keep mock user for testing
    window.location.href = '/';
  };

  const sendOTP = async (phone, email) => {
    // Mock OTP success
    return { success: true };
  };

  const verifyOTP = async (phone, email, otp) => {
    // Mock verification success
    return { success: true };
  };

  const updateProfile = async (profileData) => {
    // Mock profile update success
    return { success: true };
  };

  const updateKYC = async (kycData) => {
    // Mock KYC update success
    return { success: true };
  };

  const fetchNotifications = async () => {
    // Mock function - notifications already set in state
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/notifications/${notificationId}/read`);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to mark notification as read' 
      };
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/notifications/mark-all-read`);
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to mark all notifications as read' 
      };
    }
  };

  const value = {
    user,
    loading,
    notifications,
    unreadCount,
    register,
    login,
    logout,
    sendOTP,
    verifyOTP,
    updateProfile,
    updateKYC,
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    refreshUser: checkAuthStatus
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};